import React from 'react';
import { useLocation } from 'react-router-dom';

const specs = [
  { label: 'Origin', value: 'Single Origin Uganda' },
  { label: 'Region', value: 'Luweero District, Central Uganda' },
  { label: 'Varietal', value: 'Robusta' },
  { label: 'Process', value: 'Natural, sun dried on raised beds' },
  { label: 'Harvest Date', value: 'February 2026' },
  { label: 'Altitude', value: '1,100 to 1,200 MASL' },
  { label: 'Moisture Level', value: '11.8%' },
  { label: 'Profile', value: 'Raw green beans' },
  { label: 'Cupping Method', value: 'Mokapot' },
  { label: 'Best For', value: 'Espresso shots & blends' },
  { label: 'Tasting Notes', value: 'Dark chocolate, woody, earthy, full body' },
  { label: 'Brew Recipe', value: 'Pour over V60, 13g coffee to 230g water' }
];

const grades = [
  {
    code: 'SC-18',
    name: 'Screen 18',
    price: '3,300',
    blurb: 'Beans held by a 7.1 mm (18/64 in) screen, the largest grade. For top-shelf espresso and showcase blends.',
    highlight: true
  },
  {
    code: 'SC-15',
    name: 'Screen 15',
    price: '3,000',
    blurb: 'Beans held by a 6.0 mm (15/64 in) screen. Reliable cup quality for café house blends and daily roasts.',
    highlight: false
  },
  {
    code: 'SC-12',
    name: 'Screen 12',
    price: '2,700',
    blurb: 'Beans held by a 4.8 mm (12/64 in) screen. Strong value for high-volume blends and instant production.',
    highlight: false
  }
];

export default function CoffeeSpecsAndPricing() {
  const onWholesalePage = useLocation().pathname === '/wholesale';

  return (
    <section id="specs-pricing" className="relative py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 lg:mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-clay mb-4">Spec Sheet & Wholesale Pricing</p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900 mb-6">
              Green bean specifications
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl leading-relaxed">
              Everything roasters and distributors need to evaluate the harvest. Full traceability, transparent pricing, stock held in Karachi.
            </p>
            {!onWholesalePage && (
              <a
                href="/wholesale"
                className="mt-6 inline-flex items-center gap-2 text-clay font-semibold hover:text-clay/80 transition-colors text-sm"
              >
                See the full wholesale page for roasters and cafés
                <span aria-hidden="true">&rarr;</span>
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Spec sheet, 7/12 */}
            <div className="lg:col-span-7 bg-white rounded-3xl border border-gray-200 overflow-hidden">
              <div className="px-6 lg:px-8 py-5 border-b border-gray-200">
                <h3 className="font-heading text-2xl text-gray-900">Origin & spec sheet</h3>
                <p className="text-sm text-gray-500 mt-1">February 2026 harvest, current lot</p>
              </div>
              <dl className="divide-y divide-gray-200/70">
                {specs.map((spec) => (
                  <div key={spec.label} className="grid grid-cols-12 gap-3 px-6 lg:px-8 py-4">
                    <dt className="col-span-12 sm:col-span-5 text-xs font-semibold uppercase tracking-wider text-gray-500 sm:pt-0.5">
                      {spec.label}
                    </dt>
                    <dd className="col-span-12 sm:col-span-7 text-base text-gray-900">
                      {spec.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Pricing, 5/12 */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <div className="bg-gray-900 rounded-3xl p-6 lg:p-8 text-white">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h3 className="font-heading text-2xl">Green bean pricing</h3>
                  <span className="flex-shrink-0 whitespace-nowrap text-xs font-semibold text-gold-400 uppercase tracking-wider">PKR / kg</span>
                </div>
                <p className="text-sm text-gray-400 mb-4">Wholesale, duty paid, Karachi delivery included. Prices updated May 2026.</p>

                <dl className="divide-y divide-gray-800 border-y border-gray-800">
                  {grades.map((grade) => (
                    <div key={grade.code} className="py-5 grid grid-cols-[1fr_auto] items-baseline gap-x-4">
                      <dt className="flex items-baseline gap-2 mb-1.5">
                        <span className="font-heading text-xl">{grade.code}</span>
                        {grade.highlight && (
                          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-400">Top grade</span>
                        )}
                      </dt>
                      <dd className="font-heading text-3xl text-gold-400 tabular-nums leading-none mb-1.5">{grade.price}</dd>
                      <dd className="col-span-2 text-sm text-gray-400 leading-relaxed">{grade.blurb}</dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-5 text-sm text-gray-300 leading-relaxed">
                  <span className="font-semibold text-white">Buying 500 kg or more?</span> Volume tiers and container-load rates are available on request.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href="https://wa.me/447716089293"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-gray-900 font-bold px-4 py-3.5 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97] text-sm"
                >
                  Request Sample
                </a>
                <a
                  href={onWholesalePage ? '#b2b-contact' : '/wholesale'}
                  className="inline-flex items-center justify-center bg-gray-900 hover:bg-clay text-white font-bold px-4 py-3.5 rounded-full transition-[background-color,transform] duration-200 active:scale-[0.97] text-sm"
                >
                  {onWholesalePage ? 'Talk to wholesale desk' : 'Order Bulk'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
